import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import {
    type TenantContactKind,
    type TenantContactRecord,
    useCreateContact,
    useUpdateContact,
} from "~/lib/tenants";

interface ContactDialogProps {
    tenantId: string;
    contact: TenantContactRecord | null; // null = create
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const KINDS: ReadonlyArray<{ value: TenantContactKind; label: string }> = [
    { value: "primary",   label: "Primary" },
    { value: "billing",   label: "Billing" },
    { value: "technical", label: "Technical" },
    { value: "legal",     label: "Legal" },
    { value: "security",  label: "Security" },
];

export function ContactDialog({
    tenantId,
    contact,
    open,
    onOpenChange,
}: ContactDialogProps): React.ReactElement {
    const create = useCreateContact();
    const update = useUpdateContact();
    const pending = create.isPending || update.isPending;

    const [kind, setKind] = useState<TenantContactKind>(contact?.kind ?? "primary");
    const [name, setName] = useState(contact?.name ?? "");
    const [email, setEmail] = useState(contact?.email ?? "");
    const [phone, setPhone] = useState(contact?.phone ?? "");
    const [title, setTitle] = useState(contact?.title ?? "");
    const [notes, setNotes] = useState(contact?.notes ?? "");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setKind(contact?.kind ?? "primary");
        setName(contact?.name ?? "");
        setEmail(contact?.email ?? "");
        setPhone(contact?.phone ?? "");
        setTitle(contact?.title ?? "");
        setNotes(contact?.notes ?? "");
        setError(null);
        create.reset();
        update.reset();
    }, [open, contact]); // eslint-disable-line react-hooks/exhaustive-deps

    async function onSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);
        const trimmedName = name.trim();
        const trimmedEmail = email.trim();
        if (!trimmedName) {
            setError("Name required.");
            return;
        }
        if (!EMAIL_RE.test(trimmedEmail)) {
            setError("Email must be a valid address.");
            return;
        }
        try {
            if (contact) {
                await update.mutateAsync({
                    tenantId,
                    id: contact.id,
                    kind,
                    name: trimmedName,
                    email: trimmedEmail,
                    phone,
                    title,
                    notes,
                });
                toast.success("Contact updated");
            } else {
                await create.mutateAsync({
                    tenantId,
                    kind,
                    name: trimmedName,
                    email: trimmedEmail,
                    phone: phone || undefined,
                    title: title || undefined,
                    notes: notes || undefined,
                });
                toast.success("Contact added");
            }
            onOpenChange(false);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to save.");
        }
    }

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content
                        className="dialog-content enter-up"
                        style={{ padding: "var(--space-6)" }}
                    >
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">
                                {contact ? "Edit contact" : "Add contact"}
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        <Dialog.Description asChild>
                            <form onSubmit={onSubmit} className="stack" style={{ marginTop: "var(--space-4)" }}>
                                <label className="stack-xs">
                                    <span className="text-12 text-text-muted">Kind</span>
                                    <select
                                        className="input"
                                        value={kind}
                                        onChange={(e) => setKind(e.target.value as TenantContactKind)}
                                    >
                                        {KINDS.map((k) => (
                                            <option key={k.value} value={k.value}>
                                                {k.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="stack-xs">
                                    <span className="text-12 text-text-muted">Name</span>
                                    <input
                                        autoFocus
                                        required
                                        className="input"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </label>
                                <label className="stack-xs">
                                    <span className="text-12 text-text-muted">Email</span>
                                    <input
                                        required
                                        type="email"
                                        className="input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </label>
                                <label className="stack-xs">
                                    <span className="text-12 text-text-muted">Title</span>
                                    <input
                                        className="input"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="CISO"
                                    />
                                </label>
                                <label className="stack-xs">
                                    <span className="text-12 text-text-muted">Phone</span>
                                    <input
                                        className="input"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+90 555 000 0000"
                                    />
                                </label>
                                <label className="stack-xs">
                                    <span className="text-12 text-text-muted">Notes</span>
                                    <textarea
                                        className="input"
                                        rows={2}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </label>

                                {error && (
                                    <div
                                        className="chip chip--danger shake"
                                        style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                        role="alert"
                                    >
                                        {error}
                                    </div>
                                )}

                                <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                                    <Dialog.Close asChild>
                                        <button type="button" className="btn btn--ghost">Cancel</button>
                                    </Dialog.Close>
                                    <button type="submit" className="btn btn--primary press-crush" disabled={pending}>
                                        {pending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                        <span>{pending ? "Saving…" : contact ? "Save" : "Add contact"}</span>
                                    </button>
                                </div>
                            </form>
                        </Dialog.Description>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
